// File: src/pages/AboutPage.js
import React from 'react';
import { Helmet } from 'react-helmet-async';
// import './AboutPage.css'; // Remove CSS import
import {
    Layout, Row, Col, Typography, Space, Divider, Image, Card, Alert, Collapse, Button, Avatar, Table
} from 'antd';
import { SearchOutlined, QuestionCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, ShopOutlined, UserAddOutlined, InfoCircleOutlined } from '@ant-design/icons';

// Import Link from react-router-dom
import { Link } from 'react-router-dom';

const { Content } = Layout;
// Rename Ant Design's Link to AntLink to avoid conflict
const { Title, Paragraph, Text, Link: AntLinkAnchor } = Typography;
const { Panel } = Collapse;

const AboutPage = () => {

    // FAQ Items (Legal question removed, moved to own section)
    const faqItems = [
        {
            key: 'faq-1', // Ensure unique keys if mixing with other Collapse panels
            label: 'Wie verwendet man Snus richtig?',
            children: <Paragraph>Bei Portionssnus öffnen Sie die Dose, entnehmen einen Beutel und platzieren ihn zwischen Oberlippe und Zahnfleisch. Bei losem Snus formen Sie zunächst mit den Fingern oder einem speziellen Tool (Prismaster) eine kleine Portion. Die Wirkung setzt innerhalb weniger Minuten ein und hält je nach Stärke 30-60 Minuten an.</Paragraph>,
        },
        {
            key: 'faq-2',
            label: 'Ist Snus eine gute Alternative zum Rauchen?',
            children: <Paragraph>Viele Ex-Raucher, insbesondere in Schweden, haben erfolgreich auf Snus umgestellt. Da keine Verbrennung stattfindet, werden viele der schädlichsten Stoffe des Rauchens vermieden. Dennoch bleibt Snus ein Produkt mit Gesundheitsrisiken und Suchtpotenzial. Die beste Option für die Gesundheit ist und bleibt der vollständige Verzicht auf Nikotin.</Paragraph>,
        },
        {
            key: 'faq-3',
            label: 'Wie lange ist Snus haltbar?',
            children: <Paragraph>Die meisten Snus-Hersteller geben eine Haltbarkeit von etwa 10-14 Wochen an. Nach dem Öffnen sollte Snus im Kühlschrank aufbewahrt werden, um Frische und Geschmack zu bewahren. Bei richtiger Lagerung kann geöffneter Snus etwa 1-2 Wochen seine optimale Qualität behalten.</Paragraph>,
        },
        {
            key: 'faq-4',
            label: 'Was sind die Unterschiede zwischen Snus und Kautabak?',
            children: <Paragraph>Obwohl beide Produkte oral konsumiert werden, gibt es wesentliche Unterschiede. Snus wird pasteurisiert, was Bakterien abtötet und den Gehalt an krebserregenden Nitrosaminen reduziert. Kautabak wird hingegen fermentiert. Snus wird zwischen Oberlippe und Zahnfleisch platziert und nicht gekaut. Der Speichel wird normalerweise nicht ausgespuckt, anders als bei amerikanischem Kautabak.</Paragraph>,
        },
    ];

    // Comparison Table Data
    const comparisonColumns = [
        { title: 'Merkmal', dataIndex: 'feature', key: 'feature' }, // Removed fixed width
        { title: 'Snus (Traditionell)', dataIndex: 'snus', key: 'snus' },
        { title: 'Nikotinbeutel', dataIndex: 'nicotinePouch', key: 'nicotinePouch' },
        { title: 'Zigarette', dataIndex: 'cigarette', key: 'cigarette' },
    ];

    const comparisonData = [
        {
            key: 'comp-1', // Ensure unique keys
            feature: <Text strong>Hauptinhaltsstoff</Text>,
            snus: 'Tabak, Wasser, Salz, Aromen',
            nicotinePouch: 'Pflanzenfasern, Nikotin, Wasser, Aromen',
            cigarette: 'Tabak, Papier, Filter, Zusatzstoffe',
        },
        {
            key: 'comp-2',
            feature: <Text strong>Konsumart</Text>,
            snus: 'Oral (unter der Oberlippe)',
            nicotinePouch: 'Oral (unter der Oberlippe)',
            cigarette: 'Inhalation (Rauchen)',
        },
        {
            key: 'comp-3',
            feature: <Text strong>Verbrennung</Text>,
            snus: <Text type="success"><CloseCircleOutlined /> Nein</Text>,
            nicotinePouch: <Text type="success"><CloseCircleOutlined /> Nein</Text>,
            cigarette: <Text type="danger"><CheckCircleOutlined /> Ja</Text>,
        },
        {
            key: 'comp-4',
            feature: <Text strong>Hauptrisiko (Lunge)</Text>,
            snus: <Text type="success">Gering</Text>,
            nicotinePouch: <Text type="success">Gering</Text>,
            cigarette: <Text type="danger">Sehr Hoch (Krebs, COPD)</Text>,
        },
         {
            key: 'comp-5',
            feature: <Text strong>Hauptrisiko (Mundraum)</Text>,
            snus: <Text type="warning">Deutliche Risiken (Zahnfleisch, Läsionen), geringeres Krebsrisiko als Rauchen</Text>,
            nicotinePouch: <Text type="warning">Möglich (Irritationen), Langzeitrisiken unklar</Text>,
            cigarette: <Text type="danger">Hoch (Krebs, Parodontitis)</Text>,
        },
         {
            key: 'comp-6',
            feature: <Text strong>Suchtpotenzial</Text>,
            snus: <Text type="danger">Hoch (Nikotin)</Text>,
            nicotinePouch: <Text type="danger">Hoch (Nikotin)</Text>,
            cigarette: <Text type="danger">Sehr Hoch (Nikotin + Rituale)</Text>,
        },
        {
            key: 'comp-7',
            feature: <Text strong>Verkauf in DE/EU</Text>,
            snus: <Text type="danger"><CloseCircleOutlined /> Verboten (außer SE)</Text>,
            nicotinePouch: <Text type="success"><CheckCircleOutlined /> Erlaubt</Text>,
            cigarette: <Text type="success"><CheckCircleOutlined /> Erlaubt (reguliert)</Text>,
        },
    ];
    
    // Glossary Items
    const glossaryItems = [
        { term: 'Prilla/Pris', description: 'Eine einzelne Portion Snus (lose oder im Beutel).' },
        { term: 'White Portion', description: 'Portionssnus mit trockener Oberfläche, weniger "Rinnen".' },
        { term: 'Original Portion', description: 'Portionssnus mit befeuchteter Oberfläche für schnellere Freisetzung.' },
        { term: 'All White', description: 'Tabakfreie Nikotinbeutel, oft mit weißem Inhalt.' },
        { term: 'Stark/Sterk', description: 'Bezeichnung für Snus mit höherem Nikotingehalt.' },
        { term: 'Prismaster', description: 'Werkzeug zum Formen von losem Snus zu einer Portion.' },
        { term: 'mg/g', description: 'Milligramm Nikotin pro Gramm Snus (übliche Angabe der Stärke).' },
        { term: 'TPD', description: 'EU-Tabakproduktrichtlinie (Tobacco Products Directive).' },
    ];

    // Example Brands Data
    const exampleBrands = [
        { name: 'General', logo: 'G', desc: 'Klassiker mit Bergamotte.' }, // Shortened desc
        { name: 'Lyft/Velo', logo: 'V', desc: 'Moderne Nikotinbeutel.' }, // Updated logo & desc
        { name: 'Siberia', logo: 'S', desc: 'Extrem stark.' }, // Shortened desc
        { name: 'Zyn', logo: 'Z', desc: 'Tabakfreie Beutel.' }, // Shortened desc
        { name: 'Skruf', logo: 'S', desc: 'Ausgewogene Profile.' }, // Shortened desc
        { name: 'White Fox', logo: 'W', desc: 'Starke Minz-Optionen.' } // Added another brand
    ];

    return (
        <Layout>
            <Helmet>
                 <title>Über Snus Verzeichnis Deutschland | Unsere Mission & Infos</title> {/* Updated Title */}
                 <meta name="description" content="Erfahren Sie mehr über Snus Verzeichnis Deutschland, das erste Verzeichnis für Snus-Läden in Deutschland. Infos zu Snus, Nikotinbeuteln, Rechtslage & mehr." /> {/* Updated Desc */}
                 <meta name="keywords" content="snus verzeichnis, snus shops deutschland, snus läden finden, snus kaufen, snus geschäft melden, snus shop registrieren, about snus finder, was ist snus, snus arten, snus legal, nikotinbeutel" /> {/* Updated Keywords */}
                 <link rel="canonical" href="https://www.snus-finder.de/about" /> {/* Added Canonical Tag */}
            </Helmet>

             {/* Use Content with max-width and auto margin for centering, similar to HomePage */}
            <Content style={{ maxWidth: '1000px', margin: '30px auto', padding: '0 16px' }}>

                {/* Hero Title Section - Simplified, no background image */}
                <Row justify="center" style={{ marginBottom: '30px', textAlign: 'center' }}>
                    <Col xs={24} md={20}>
                         <Title level={1} style={{ marginBottom: '0.5rem' }}>Über Snus Verzeichnis Deutschland</Title>
                         <Paragraph style={{ fontSize: '1.1em', color: 'rgba(0, 0, 0, 0.65)' }}>
                             Die erste Anlaufstelle für Snus-Shops & Infos in Deutschland
                         </Paragraph>
                    </Col>
                </Row>

                 {/* Section: Unser Verzeichnis */}
                 <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card id="unser-verzeichnis">
                            <Title level={2} style={{ textAlign: 'center' }}><InfoCircleOutlined /> Unser Verzeichnis</Title>
                            <Paragraph style={{ textAlign: 'center', marginBottom: '2rem' }}>
                                Willkommen bei Snus Verzeichnis Deutschland! Unsere Mission ist es, eine umfassende und aktuelle Datenbank für Konsumenten aufzubauen und gleichzeitig Shop-Besitzern eine Plattform zu bieten.
                            </Paragraph>
                            <Row gutter={[16, 24]}> {/* Increased vertical gutter */}
                                <Col xs={24} lg={12}>
                                    <Card type="inner" title={<><SearchOutlined /> Für Nutzer</>} style={{ height: '100%' }}>
                                        <Paragraph>
                                            Suchen Sie nach Läden in Ihrer Umgebung, die Snus oder Nikotinbeutel anbieten.
                                        </Paragraph>
                                        <Paragraph>
                                            Fehlt ein Shop? Helfen Sie der Community und melden Sie ihn einfach:
                                        </Paragraph>
                                        {/* Wrap button in Link for client-side routing */}
                                        <Link to="/shop-melden" style={{ display: 'block' }}>
                                            <Button type="primary" ghost icon={<ShopOutlined />} block> {/* Removed href */}
                                                Neuen Shop melden
                                            </Button>
                                        </Link>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                     <Card type="inner" title={<><ShopOutlined /> Für Shop-Besitzer</>} style={{ height: '100%' }}>
                                        <Paragraph>
                                            Registrieren Sie sich kostenlos, um Ihren Shop zu verwalten:
                                        </Paragraph>
                                        <ul style={{ paddingLeft: '20px', marginBottom: '1rem' }}> {/* Added padding */}
                                            <li><Text strong>Shop bereits gemeldet?</Text> <Link to="/shops">Suchen</Link>, <Link to="/register">registrieren</Link> & beanspruchen.</li>
                                            <li><Text strong>Shop noch nicht da?</Text> <Link to="/register">Registrieren</Link> & direkt anlegen.</li>
                                        </ul>
                                        {/* Wrap button in Link for client-side routing */}
                                         <Link to="/register" style={{ display: 'block' }}>
                                            <Button type="primary" icon={<UserAddOutlined />} block> {/* Removed href */}
                                                Registrieren / Shop beanspruchen
                                            </Button>
                                        </Link>
                                    </Card>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                 </Row>

                {/* Section: Was ist Snus? */}
                <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card id="was-ist-snus">
                            <Title level={2}>Was ist Snus?</Title>
                            <Paragraph>
                                Snus ist ein rauchfreies Tabakprodukt (Ursprung: 18. Jh., Schweden). Es wird meist zwischen Oberlippe und Zahnfleisch platziert, Nikotin wird über die Mundschleimhaut aufgenommen.
                            </Paragraph>
                            <Paragraph>
                                Man unterscheidet heute oft:
                            </Paragraph>
                            <ul style={{ paddingLeft: '20px' }}> {/* Added padding */}
                                <li><Text strong>Traditioneller Snus:</Text> Enthält Tabak (lose oder in Portionen).</li>
                                <li><Text strong>Nikotinbeutel (Nicotine Pouches / All White):</Text> Tabakfrei, mit Nikotin, Füllstoffen (z.B. Pflanzenfasern) und Aromen. Oft weiß, verfärben Zähne weniger.</li>
                            </ul>
                             <Paragraph>
                                Wenn wir "Snus" sagen, meinen wir oft beide Varianten, weisen aber auf Unterschiede hin (z.B. Rechtslage).
                             </Paragraph>
                        </Card>
                    </Col>
                </Row>

                 {/* Section: Vergleich */}
                 <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card id="vergleich">
                           <Title level={2}>Snus vs. Nikotinbeutel vs. Zigaretten</Title>
                           <Paragraph>
                                Unterschiede in Herstellung, Konsum und Risikoprofil:
                           </Paragraph>
                           {/* Wrap table in div for horizontal scrolling on small screens */}
                           <div style={{ overflowX: 'auto', width: '100%' }}> 
                               <Table 
                                    columns={comparisonColumns} 
                                    dataSource={comparisonData} 
                                    pagination={false} 
                                    bordered
                                    size="small"
                                />
                            </div>
                        </Card>
                    </Col>
                 </Row>

                {/* Section: Geschichte & Herstellung (Combined for brevity) */}
                 <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card id="geschichte-herstellung">
                            <Title level={2}>Geschichte & Herstellung</Title>
                            <Row gutter={[24, 16]} align="top"> {/* Align top */}
                                <Col xs={24} lg={12}>
                                    <Title level={4}>Geschichte</Title>
                                    <Paragraph>
                                        Tabak kam im 16. Jh. nach Europa. Im 18. Jh. entstand Snus in Schweden. Lange nur in Skandinavien verbreitet. Trug zur niedrigen Raucherquote in Schweden bei.
                                    </Paragraph>
                                </Col>
                                <Col xs={24} lg={12}>
                                     <Title level={4}>Herstellung</Title>
                                     <Paragraph>
                                        <Text strong>Traditioneller Snus:</Text> Tabak mahlen, pasteurisieren, aromatisieren (Salz, Wasser, Aromen), reifen, verpacken (lose/Portionen).
                                    </Paragraph>
                                     <Paragraph>
                                        <Text strong>Nikotinbeutel:</Text> Ähnlich, aber ohne Tabak (Pflanzenfasern + Nikotin + Aromen).
                                     </Paragraph>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                 </Row>

                {/* Section: Arten, Stärken & Formate */}
                 <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card id="arten-staerken">
                            <Title level={2}>Arten, Stärken & Formate</Title>
                             <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12} md={8}>
                                    <Card type="inner" title="Stärke (mg/g)" size="small" style={{ height: '100%' }}>
                                         <ul style={{ paddingLeft: '20px', marginBottom: 0 }}>
                                            <li>Normal (ca. 8-12)</li>
                                            <li>Stark (ca. 12-16)</li>
                                            <li>Extra Stark (&gt;16)</li>
                                            <li>Extrem (&gt;20)</li>
                                         </ul>
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Card type="inner" title="Geschmack" size="small" style={{ height: '100%' }}>
                                        <ul style={{ paddingLeft: '20px', marginBottom: 0 }}>
                                            <li>Klassisch (Tabak...)</li>
                                            <li>Minze / Menthol</li>
                                            <li>Frucht</li>
                                            <li>Andere (Lakritz etc.)</li>
                                        </ul>
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Card type="inner" title="Format" size="small" style={{ height: '100%' }}>
                                        <ul style={{ paddingLeft: '20px', marginBottom: 0 }}>
                                            <li>Loose (Loser Snus)</li>
                                            <li>Portion (Beutel)</li>
                                            <li>Größen: Mini, Slim, Large</li>
                                            <li>Feuchtigkeit: White, Original</li>
                                        </ul>
                                    </Card>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                 </Row>

                {/* Section: Gesundheit */}
                 <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card id="gesundheit">
                           <Title level={2}>Gesundheitliche Aspekte</Title>
                           <Alert 
                                message="Wichtiger Hinweis"
                                description="Diese Informationen ersetzen keine ärztliche oder zahnärztliche Beratung. Konsultieren Sie bei Gesundheitsfragen Fachpersonal."
                                type="info"
                                showIcon 
                                style={{ marginBottom: '1.5rem' }}
                            />
                            <Paragraph>
                                <Text strong>Geringeres Risiko als Rauchen:</Text> Keine Verbrennung (kein Teer etc.). Risiko für Lungenkrebs/Herz-Kreislauf geringer.
                            </Paragraph>
                            <Paragraph>
                                <Text strong>Risiken (Nikotin, Snus & Nikotinbeutel):</Text>
                            </Paragraph>
                            <ul style={{ paddingLeft: '20px' }}>
                                <li>Nikotin macht stark abhängig (gilt für alle Produkte).</li>
                                <li>Snus: Deutlich erhöhtes Risiko für Zahnfleischprobleme (z.B. Rückgang) und orale Läsionen (z.B. Leukoplakie).</li>
                                <li>Snus: Geringeres Risiko für Mund-/Rachenkrebs als Rauchen, aber höheres Risiko als Nichtkonsum.</li>
                                <li>Snus: Möglicherweise erhöhtes Risiko für Bauchspeicheldrüsenkrebs.</li>
                                <li>Nikotinbeutel: Weniger Daten verfügbar, aber lokale Irritationen möglich. Langzeitfolgen noch nicht vollständig erforscht.</li>
                                <li>Alle nikotinhaltigen Produkte sind schädlich in der Schwangerschaft.</li>
                            </ul>
                             <Paragraph>
                                Nikotinbeutel (tabakfrei) vermeiden tabakspezifische Risiken (wie bestimmte krebserregende Stoffe im Tabak), enthalten aber suchterzeugendes Nikotin und sind nicht risikofrei.
                             </Paragraph>
                        </Card>
                    </Col>
                 </Row>

                {/* Section: Rechtslage */}
                 <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card id="legal">
                            <Title level={2}>Rechtliche Situation (DE/EU)</Title>
                             <Row gutter={[16, 16]}>
                                <Col xs={24} lg={12}>
                                    <Card type="inner" title={<><WarningOutlined /> Traditioneller Snus (tabakhaltig)</>} style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f', height: '100%' }}>
                                        <Paragraph>
                                            Verkauf EU-weit <Text strong>verboten</Text> (Ausnahme: Schweden).
                                        </Paragraph>
                                         <Paragraph>
                                            In DE: Verkauf illegal. Besitz/Konsum nicht strafbar. Privater Import = Grauzone.
                                        </Paragraph>
                                    </Card>
                                </Col>
                                 <Col xs={24} lg={12}>
                                     <Card type="inner" title={<><CheckCircleOutlined /> Nikotinbeutel (tabakfrei)</>} style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', height: '100%' }}>
                                        <Paragraph>
                                            Fallen <Text strong>nicht</Text> unter das Verkaufsverbot (kein Tabak).
                                        </Paragraph>
                                        <Paragraph>
                                             Verkauf/Erwerb in DE <Text strong>legal</Text>. Unterliegen Verbraucherschutz (z.B. Jugendschutz).
                                        </Paragraph>
                                    </Card>
                                </Col>
                            </Row>
                             <Paragraph style={{marginTop: '1rem'}}>
                                <Text type="secondary" style={{ fontSize: '0.85em' }}>Hinweis: Keine Rechtsberatung. Stand kann sich ändern.</Text>
                            </Paragraph>
                        </Card>
                    </Col>
                 </Row>

                {/* Section: FAQ */}
                 <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card id="faq">
                            <Title level={2}>FAQ</Title>
                            {/* Use default Collapse style */} 
                            <Collapse items={faqItems} /> 
                        </Card>
                    </Col>
                 </Row>

                {/* Section: Marken */}
                 <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card id="marken">
                           <Title level={2}>Beliebte Marken (Beispiele)</Title>
                           <Row gutter={[16, 16]} justify="center"> {/* Center align brands */}
                                {exampleBrands.map((brand, index) => (
                                     <Col key={index} xs={12} sm={8} md={6} lg={4}>
                                        <Card 
                                            hoverable 
                                            style={{ textAlign: 'center', height: '100%' }} 
                                            bodyStyle={{ padding: '12px' }} 
                                        >
                                             <Avatar 
                                                size={48}
                                                style={{ backgroundColor: '#0066cc', marginBottom: '0.5rem' }}
                                             >
                                                {brand.logo}
                                            </Avatar>
                                             <Card.Meta 
                                                title={<Text strong style={{ fontSize: '0.9rem'}}>{brand.name}</Text>}
                                                description={<Text type="secondary" style={{ fontSize: '0.8rem' }}>{brand.desc}</Text>} 
                                            />
                                         </Card>
                                    </Col>
                                ))}
                           </Row>
                        </Card>
                    </Col>
                 </Row>

                {/* Section: Glossar */}
                 <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card id="glossar"> 
                            <Title level={2}>Glossar</Title>
                            <Row gutter={[16, 8]}> 
                                {glossaryItems.map(item => (
                                    <Col xs={24} sm={12} md={8} key={item.term}>
                                        <Text strong>{item.term}:</Text> <Text>{item.description}</Text>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    </Col>
                 </Row>

                {/* Section: Call to Action Button */}
                <Row justify="center" style={{ marginTop: '1rem', marginBottom: '50px' }}>
                    <Col>
                         <Button type="primary" size="large" icon={<SearchOutlined />} href="/shops">
                             Jetzt Snus Shops finden!
                         </Button>
                    </Col>
                 </Row>

            </Content>
        </Layout>
    );
};

export default AboutPage;